// src/components/nutrition/vitamins/nutritionVitamin.jsx
const VITAMIN_LABELS = {
  biotin: "Biotin (B7)",
  choline: "Choline",
  folate: "Folate (B9)",
  folateB12: "Folate + B12 (combo)",
  niacin: "Niacin (B3)",
  pantothenicAcid: "Pantothenic Acid (B5)",
  riboflavin: "Riboflavin (B2)",
  thiamin: "Thiamin (B1)",
  vitaminA: "Vitamin A",
  vitaminB12: "Vitamin B12",
  vitaminB6: "Vitamin B6",
  vitaminC: "Vitamin C",
  vitaminD: "Vitamin D",
  vitaminE: "Vitamin E",
  vitaminK: "Vitamin K",
};

export const VITAMIN_IDS = Object.keys(VITAMIN_LABELS);

export const VITAMIN_FOCUS_IDS = [
  "vitaminD",
  "vitaminB6",
  "vitaminA",
  "vitaminC",
  "vitaminE",
  "vitaminK",
  "folate",
  "vitaminB12",
  "choline",
  "thiamin",
  "riboflavin",
  "niacin",
  "pantothenicAcid",
];

const VITAMIN_OVERVIEW_IDS = [
  "biotin",
  "choline",
  "folate",
  "niacin",
  "pantothenicAcid",
  "riboflavin",
  "thiamin",
  "vitaminA",
  "vitaminB12",
  "vitaminB6",
  "vitaminC",
  "vitaminD",
  "vitaminE",
  "vitaminK",
];

export const vitaminOptions = VITAMIN_IDS.map((vitaminId) => ({
  id: vitaminId,
  label: VITAMIN_LABELS[vitaminId],
}));

export const vitaminInitialData = {
  biotin: {
    todayDV: null,
    avgDV: null,
    mcg: null,
  },
  choline: {
    todayDV: null,
    avgDV: null,
    mg: null,
  },
  folate: {
    todayDV: null,
    avgDV: null,
    mcg: null,
  },
  folateB12: {
    folateDV: null,
    b12DV: null,
    folateMcg: null,
    b12Mcg: null,
    todayDV: null,
    avgDV: null,
    mcg: null,
  },
  niacin: {
    todayDV: null,
    avgDV: null,
    mg: null,
  },
  pantothenicAcid: {
    todayDV: null,
    avgDV: null,
    mg: null,
  },
  riboflavin: {
    todayDV: null,
    avgDV: null,
    mg: null,
  },
  thiamin: {
    todayDV: null,
    avgDV: null,
    mg: null,
  },
  vitaminA: {
    todayDV: null,
    avgDV: null,
    rae: null,
    topSource: "Carrots",
  },
  vitaminB12: {
    todayDV: null,
    avgDV: null,
    mcg: null,
  },
  vitaminB6: {
    todayDV: null,
    avgDV: null,
    mg: null,
  },
  vitaminC: {
    todayDV: null,
    avgDV: null,
    mg: null,
  },
  vitaminD: {
    todayDV: null,
    avgDV: null,
    iu: null,
    goal: 800,
    season: "Winter",
  },
  vitaminE: {
    todayDV: null,
    avgDV: null,
    mg: null,
  },
  vitaminK: {
    todayDV: null,
    avgDV: null,
    mcg: null,
    type: "K1",
  },
};

export function getVitaminsGroup() {
  return {
    name: "Vitamins",
    items: vitaminOptions,
  };
}

export function getVitaminStatuses(
  data,
  { dvStatusFn, low = 80, high = 120 } = {}
) {
  const vitaminData = data || {};
  const statuses = {};

  VITAMIN_IDS.forEach((vitaminId) => {
    if (vitaminId === "folateB12") return;
    statuses[vitaminId] = dvStatusFn?.(vitaminData[vitaminId]?.todayDV);
  });

  const combo = vitaminData.folateB12;
  if (combo && combo.todayDV != null) {
    statuses.folateB12 = dvStatusFn?.(combo.todayDV);
  } else {
    const folateDvValue = combo?.folateDV ?? NaN;
    const vitaminB12DvValue = combo?.b12DV ?? NaN;
    statuses.folateB12 =
      !Number.isFinite(folateDvValue) || !Number.isFinite(vitaminB12DvValue)
        ? "data-needed"
        : folateDvValue < low
        ? "low"
        : vitaminB12DvValue > high
        ? "high"
        : "on-track";
  }

  return statuses;
}

export function getVitaminsOverviewMeta(data) {
  const vitaminData = data || {};

  const dvValues = VITAMIN_OVERVIEW_IDS
    .map((vitaminId) => vitaminData[vitaminId]?.todayDV)
    .filter((value) => typeof value === "number" && Number.isFinite(value));

  if (dvValues.length === 0) {
    return { id: "vitamins", label: "Vitamins", meta: "–" };
  }

  const averageDv =
    dvValues.reduce((sum, value) => sum + value, 0) / dvValues.length;
  const roundedAverageDv = Math.round(averageDv);

  const overviewMeta = {
    id: "vitamins",
    label: "Vitamins",
    meta: `${roundedAverageDv}%`,
  };

  return overviewMeta;
}

export function getVitaminQuickAddBehaviorFor(vitaminId) {
  if (vitaminId === "vitaminD") {
    return {
      mode: "dose",
      unit: "IU",
      unitLabel: "IU Vitamin D",
      deltaPct: null,
    };
  }
  return null;
}

export function getVitaminQuickAddDefaults(targetId) {
  if (targetId === "vitaminD") {
    return { amount: 1000 };
  }
  return null;
}

export function buildVitaminSpecialTile(vitaminId, node, statuses, label) {
  if (vitaminId === "folateB12") return null;

  return null;
}

export function getVitaminLogAction(vitaminId) {
  if (vitaminId === "vitaminD") {
    return {
      mode: "dose",
      nutrientId: vitaminId,
      amount: 1000,
      unit: "IU",
    };
  }
  return null;
}

export function getVitaminChartData(data, { low = 80, high = 120 } = {}) {
  const vitaminData = data || {};
  const lowVitamins = [];
  const onGoalVitamins = [];
  const highVitamins = [];

  const chartIds = VITAMIN_IDS.filter((vitaminId) => vitaminId !== "folateB12");

  chartIds.forEach((vitaminId) => {
    const nutrient = vitaminData[vitaminId];
    if (!nutrient || nutrient.todayDV == null) return;

    const dvValue = nutrient.todayDV;
    const label = VITAMIN_LABELS[vitaminId];

    if (dvValue < low) {
      lowVitamins.push({ id: vitaminId, label, dv: dvValue });
    } else if (dvValue > high) {
      highVitamins.push({ id: vitaminId, label, dv: dvValue });
    } else {
      onGoalVitamins.push({ id: vitaminId, label, dv: dvValue });
    }
  });

  const folateB12 = vitaminData.folateB12;
  if (folateB12 && folateB12.todayDV != null) {
    const label = VITAMIN_LABELS.folateB12;
    const dvValue = folateB12.todayDV;

    if (dvValue < low) {
      lowVitamins.push({ id: "folateB12", label, dv: dvValue });
    } else if (dvValue > high) {
      highVitamins.push({ id: "folateB12", label, dv: dvValue });
    } else {
      onGoalVitamins.push({ id: "folateB12", label, dv: dvValue });
    }
  }

  const chartData = {
    low: lowVitamins,
    onGoal: onGoalVitamins,
    high: highVitamins,
    category: "vitamins",
    categoryLabel: "Vitamins",
  };

  return chartData;
}

export function getVitaminIdsForChart() {
  return VITAMIN_IDS.filter((vitaminId) => vitaminId !== "folateB12");
}

export function getVitaminLabel(vitaminId) {
  return VITAMIN_LABELS[vitaminId] || vitaminId;
}

const VITAMIN_SNAPSHOT_META = {
  vitaminA: { baseKey: "vitaminA", amountField: "rae" },
  vitaminD: { baseKey: "vitaminD", amountField: "iu", convert: "ugToIU_D" },
  vitaminE: { baseKey: "vitaminE", amountField: "mg" },
  vitaminK: { baseKey: "vitaminK", amountField: "mcg" },
  vitaminC: { baseKey: "vitaminC", amountField: "mg" },
  thiamin: { baseKey: "thiamin", amountField: "mg" },
  riboflavin: { baseKey: "riboflavin", amountField: "mg" },
  niacin: { baseKey: "niacin", amountField: "mg" },
  vitaminB6: { baseKey: "vitaminB6", amountField: "mg" },
  folate: { baseKey: "folate", amountField: "mcg" },
  vitaminB12: { baseKey: "vitaminB12", amountField: "mcg" },
  biotin: { baseKey: "biotin", amountField: "mcg" },
  pantothenicAcid: { baseKey: "pantothenicAcid", amountField: "mg" },
  choline: { baseKey: "choline", amountField: "mg" },
};

function resolveSnapshotKeyForVitamin(
  baseKey,
  averageDailyIntake,
  comparisonVsTargets
) {
  if (!baseKey) {
    return null;
  }

  const comparisonKeys = Object.keys(comparisonVsTargets || {});
  const averageKeys = Object.keys(averageDailyIntake || {});
  const allKeysSet = new Set([...comparisonKeys, ...averageKeys]);
  const allKeys = Array.from(allKeysSet);

  if (allKeys.length === 0) {
    return null;
  }

  const lowerBase = String(baseKey).toLowerCase();

  const matchers = [
    (key) => key.toLowerCase() === lowerBase,
    (key) => key.toLowerCase().startsWith(`${lowerBase}_`),
    (key) =>
      key.toLowerCase().includes(`${lowerBase}_`) ||
      key.toLowerCase().includes(`${lowerBase}-`),
    (key) => key.toLowerCase().includes(lowerBase),
  ];

  for (const match of matchers) {
    const foundKey = allKeys.find(match);
    if (foundKey) {
      return foundKey;
    }
  }

  return null;
}

function createBaseVitaminDataFromInitialMeta() {
  const result = {};

  VITAMIN_IDS.forEach((vitaminId) => {
    const initialMeta = vitaminInitialData[vitaminId] || {};
    result[vitaminId] = { ...initialMeta };
  });

  return result;
}

function mergeVitaminDataIntoExisting(existingData, newData) {
  if (!existingData || typeof existingData !== "object") {
    return newData;
  }

  const mergedData = existingData;

  VITAMIN_IDS.forEach((vitaminId) => {
    const newVitaminData = newData[vitaminId] || {};
    const existingVitaminData = existingData[vitaminId] || {};

    mergedData[vitaminId] = {
      ...existingVitaminData,
      ...newVitaminData,
    };
  });

  return mergedData;
}

export function buildVitaminDataFromNutritionCalculateSnapshot(
  snapshotOrIntakeSummary,
  existingData
) {
  const vitaminDataMap = createBaseVitaminDataFromInitialMeta();

  if (!snapshotOrIntakeSummary) {
    const noPayloadResult = mergeVitaminDataIntoExisting(
      existingData,
      vitaminDataMap
    );
    return noPayloadResult;
  }

  const intakeSummary =
    snapshotOrIntakeSummary && snapshotOrIntakeSummary.intakeSummary
      ? snapshotOrIntakeSummary.intakeSummary
      : snapshotOrIntakeSummary;

  if (!intakeSummary || typeof intakeSummary !== "object") {
    const invalidSummaryResult = mergeVitaminDataIntoExisting(
      existingData,
      vitaminDataMap
    );
    return invalidSummaryResult;
  }

  const { averageDailyIntake = {}, comparisonVsTargets = {} } = intakeSummary;

  const isFiniteNumber = (value) =>
    typeof value === "number" && Number.isFinite(value);

  Object.entries(VITAMIN_SNAPSHOT_META).forEach(
    ([vitaminId, { baseKey, amountField, convert }]) => {
      const resolvedKey = resolveSnapshotKeyForVitamin(
        baseKey,
        averageDailyIntake,
        comparisonVsTargets
      );

      if (!resolvedKey) {
        return;
      }

      const comparison = comparisonVsTargets[resolvedKey];
      const averageValue = averageDailyIntake[resolvedKey];

      const actualFromComparison =
        comparison && isFiniteNumber(comparison.actual)
          ? comparison.actual
          : null;

      const actualFromAverage = isFiniteNumber(averageValue)
        ? averageValue
        : null;

      const actual =
        actualFromComparison != null ? actualFromComparison : actualFromAverage;

      const vitaminDataBeforeUpdate = vitaminDataMap[vitaminId] || {};
      const vitaminDataForVitamin = vitaminDataBeforeUpdate;
      vitaminDataMap[vitaminId] = vitaminDataForVitamin;

      if (comparison && isFiniteNumber(comparison.percentOfTarget)) {
        const roundedPercent = Math.round(comparison.percentOfTarget);
        vitaminDataForVitamin.todayDV = roundedPercent;
        vitaminDataForVitamin.avgDV = roundedPercent;
      }

      if (isFiniteNumber(actual)) {
        if (amountField === "mcg") {
          vitaminDataForVitamin.mcg = Math.round(actual);
        } else if (amountField === "mg") {
          vitaminDataForVitamin.mg = Math.round(actual * 10) / 10;
        } else if (amountField === "rae") {
          vitaminDataForVitamin.rae = Math.round(actual);
        } else if (amountField === "iu" && convert === "ugToIU_D") {
          const iuValue = Math.round(actual * 40);
          vitaminDataForVitamin.iu = iuValue;
        }
      }
    }
  );

  const folate = vitaminDataMap.folate || {};
  const vitaminB12 = vitaminDataMap.vitaminB12 || {};
  const comboBase = vitaminDataMap.folateB12 || {};

  const folateDV = folate.todayDV;
  const vitaminB12DV = vitaminB12.todayDV;
  const folateMcg = folate.mcg;
  const vitaminB12Mcg = vitaminB12.mcg;

  const folateDVFinite = isFiniteNumber(folateDV) ? folateDV : null;
  const vitaminB12DVFinite = isFiniteNumber(vitaminB12DV)
    ? vitaminB12DV
    : null;

  const dvParts = [];
  if (folateDVFinite != null) dvParts.push(folateDVFinite);
  if (vitaminB12DVFinite != null) dvParts.push(vitaminB12DVFinite);

  let comboTodayDV = null;
  if (dvParts.length === 1) {
    comboTodayDV = dvParts[0];
  } else if (dvParts.length === 2) {
    comboTodayDV = Math.round((dvParts[0] + dvParts[1]) / 2);
  }

  const combo = {
    ...comboBase,
    folateDV: folateDVFinite,
    b12DV: vitaminB12DVFinite,
    folateMcg: isFiniteNumber(folateMcg) ? folateMcg : null,
    b12Mcg: isFiniteNumber(vitaminB12Mcg) ? vitaminB12Mcg : null,
    todayDV: comboTodayDV,
    avgDV: comboTodayDV,
    mcg: isFiniteNumber(folateMcg) ? folateMcg : null,
  };

  vitaminDataMap.folateB12 = combo;

  let rangeDays = null;
  if (
    snapshotOrIntakeSummary &&
    typeof snapshotOrIntakeSummary === "object" &&
    snapshotOrIntakeSummary.rangeInfo &&
    typeof snapshotOrIntakeSummary.rangeInfo.daysInRange === "number" &&
    Number.isFinite(snapshotOrIntakeSummary.rangeInfo.daysInRange)
  ) {
    rangeDays = snapshotOrIntakeSummary.rangeInfo.daysInRange;
  }

  if (rangeDays != null) {
    VITAMIN_IDS.forEach((vitaminId) => {
      const v = vitaminDataMap[vitaminId] || (vitaminDataMap[vitaminId] = {});
      v.rangeDays = rangeDays;
    });
  }

  const finalData = mergeVitaminDataIntoExisting(existingData, vitaminDataMap);

  return finalData;
}
